import { FunctionComponent, useState, useMemo } from "react"
import { useTypedSelector } from "../../app/store"
import {
  useGetMeasuresQuery,
  useGetOrganizationMeasuresQuery,
  useGetOrganizationResultsAccessStatusQuery,
  useGetOrganizationMeasureScoresQuery,
} from "../../app/services/meseeknaApiV2"
import {
  getAIIntegrationData,
  CATEGORY_COLORS,
} from "./ai-integration-data"
import type { AIMeasureData } from "./ai-integration-data"
import { useNavigate } from "react-router-dom"
import { getMeasureColor } from "./measureColors"

type MeasureWithScore = {
  measureId: number
  measureName: string
  measureColor: string
  averageScore: number | null
  scoreCount: number
  aiData: AIMeasureData
}

const getScoreColor = (score: number): string => {
  if (score >= 85) return "bg-green-500"
  if (score >= 70) return "bg-green-400"
  if (score >= 55) return "bg-yellow-400"
  if (score >= 40) return "bg-orange-400"
  return "bg-red-400"
}

const getScoreLabel = (score: number): string => {
  if (score >= 85) return "Exceptional"
  if (score >= 70) return "Strong"
  if (score >= 55) return "Good"
  if (score >= 40) return "Developing"
  return "Emerging"
}

const AIIntegrationSection: FunctionComponent = () => {
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const orgId = Number(organization?.id) || 0
  const navigate = useNavigate()

  const [selectedMeasure, setSelectedMeasure] = useState<MeasureWithScore | null>(null)
  const [activeTab, setActiveTab] = useState<"areas" | "org" | "prompts">("areas")
  const [activeCategory, setActiveCategory] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState("")

  // Fetch unlock status
  const { data: accessStatusData, isLoading: isLoadingAccess } =
    useGetOrganizationResultsAccessStatusQuery(orgId, { skip: !orgId })

  const isUnlocked = accessStatusData?.data?.hasAccess ?? false

  // Fetch all measures (for colors)
  const { data: allMeasuresData } =
    useGetMeasuresQuery(undefined, { skip: !isUnlocked })

  // Fetch org measures
  const { data: orgMeasuresData, isLoading: isLoadingMeasures } =
    useGetOrganizationMeasuresQuery(orgId, { skip: !orgId || !isUnlocked })

  // Fetch score data (org-scoped, accessible to recruiter admin)
  const { data: scoresData, isLoading: isLoadingReports } =
    useGetOrganizationMeasureScoresQuery(orgId, { skip: !orgId || !isUnlocked })

  // Build color map from all measures
  const measureColorMap = useMemo(() => {
    const map = new Map<number, string>()
    if (allMeasuresData?.data) {
      for (const m of allMeasuresData.data) {
        if (m.measureColor) map.set(m.id, m.measureColor)
      }
    }
    return map
  }, [allMeasuresData])

  // Build list of unlocked measures with score data and AI content
  const measuresWithScores = useMemo(() => {
    if (!orgMeasuresData?.data || !scoresData?.data) return []

    // Build score lookup from org measure scores
    const scoreMap = new Map<number, { averageScore: number; scoreCount: number }>()
    for (const ms of scoresData.data) {
      scoreMap.set(ms.measureId, {
        averageScore: ms.averageScore,
        scoreCount: ms.scoreCount,
      })
    }

    const result: MeasureWithScore[] = []

    for (const om of orgMeasuresData.data) {
      if (!om.isActive || !om.measure) continue

      const aiData = getAIIntegrationData(om.measure.measureName)
      if (!aiData) continue

      const scoreEntry = scoreMap.get(om.measure.id)

      result.push({
        measureId: om.measure.id,
        measureName: om.measure.measureName,
        measureColor: getMeasureColor(om.measure.measureName, measureColorMap.get(om.measure.id)),
        averageScore: scoreEntry?.averageScore ?? null,
        scoreCount: scoreEntry?.scoreCount || 0,
        aiData,
      })
    }

    return result.sort((a, b) => a.measureName.localeCompare(b.measureName))
  }, [orgMeasuresData, scoresData, measureColorMap])

  // Filter measures
  const filteredMeasures = useMemo(() => {
    return measuresWithScores.filter((m) => {
      if (activeCategory !== "all" && m.aiData.category !== activeCategory) return false
      if (searchTerm) {
        const haystack = `${m.measureName} ${m.aiData.definition} ${m.aiData.category}`.toLowerCase()
        if (!haystack.includes(searchTerm.toLowerCase())) return false
      }
      return true
    })
  }, [measuresWithScores, activeCategory, searchTerm])

  // Get unique categories from available measures
  const categories = useMemo(() => {
    const cats = new Set(measuresWithScores.map((m) => m.aiData.category))
    return Array.from(cats).sort()
  }, [measuresWithScores])

  const isLoading = isLoadingAccess || isLoadingMeasures || isLoadingReports

  if (isLoading) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6 flex items-center justify-center">
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-4 h-4 bg-[#6182F7] rounded-full"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Empty state: not unlocked
  if (!isUnlocked) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="px-8 pt-8 max-w-6xl">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">AI Integration</h1>
        </div>
        <div className="py-12 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#6182F7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Merge AI with Your Team&apos;s Intelligence
              </h2>
              <p className="text-gray-500 leading-relaxed mb-4">
                Once your team completes their simulations, you&apos;ll unlock personalized AI integration strategies for every cognitive measure. See exactly how AI tools can amplify the specific thinking patterns your team already excels at.
              </p>
              <p className="text-gray-500 leading-relaxed mb-6">
                Each measure comes with curated AI tool categories, ready-to-use prompts, and organizational implementation strategies &mdash; all mapped to your team&apos;s actual cognitive scores.
              </p>
              <button
                onClick={() => navigate("/dashboard/recruiter-admin/users/all")}
                className="px-6 py-3 text-white text-sm font-medium rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: "#6182F7" }}
              >
                Deploy Simulation
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Empty state: unlocked but no measures with scores
  if (measuresWithScores.length === 0) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="px-8 pt-8 max-w-6xl">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">AI Integration</h1>
        </div>
        <div className="py-12 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
                <svg className="w-8 h-8 text-[#6182F7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                AI Strategies Are Almost Ready
              </h2>
              <p className="text-gray-500 leading-relaxed mb-6">
                Your measures are unlocked, but we need completed simulation data to pair AI strategies with your team&apos;s cognitive scores. Once participants finish their simulations, you&apos;ll see AI integration recommendations tailored to each measure.
              </p>
              <button
                onClick={() => navigate("/dashboard/recruiter-admin/users/all")}
                className="px-6 py-3 text-white text-sm font-medium rounded-lg transition-colors hover:opacity-90"
                style={{ backgroundColor: "#6182F7" }}
              >
                Check Simulation Progress
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const closeModal = () => {
    setSelectedMeasure(null)
    setActiveTab("areas")
  }

  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <div className="px-8 pt-8 max-w-6xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">AI Integration</h1>
        <p className="text-base text-gray-500 mb-6 max-w-3xl">
          See how AI can amplify your team&apos;s cognitive strengths. Each measure below shows your team&apos;s average score alongside curated AI tool categories, ready-to-use prompts, and organizational implementation strategies.
        </p>
      </div>

      {/* Search and Filters */}
      <div className="px-8 max-w-6xl mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search measures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6182F7] focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                activeCategory === "all"
                  ? "bg-gray-900 text-white"
                  : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
            >
              All ({measuresWithScores.length})
            </button>
            {categories.map((cat) => {
              const colors = CATEGORY_COLORS[cat]
              const count = measuresWithScores.filter((m) => m.aiData.category === cat).length
              return (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    activeCategory === cat
                      ? "bg-gray-900 text-white"
                      : `${colors?.bg || "bg-gray-50"} ${colors?.text || "text-gray-700"} hover:opacity-80`
                  }`}
                >
                  {cat} ({count})
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Measures Grid */}
      <div className="px-8 pb-12 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredMeasures.map((measure) => {
            const catColors = CATEGORY_COLORS[measure.aiData.category]
            return (
              <button
                key={measure.measureId}
                onClick={() => {
                  setSelectedMeasure(measure)
                  setActiveTab("areas")
                }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 text-left hover:shadow-md hover:border-[#6182F7]/40 transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: measure.measureColor || "#6699FF" }}
                    />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${catColors?.bg || "bg-gray-100"} ${catColors?.text || "text-gray-600"}`}>
                      {measure.aiData.category}
                    </span>
                  </div>
                  {measure.averageScore != null && (
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full text-white ${getScoreColor(measure.averageScore)}`}>
                      {measure.averageScore}
                    </span>
                  )}
                </div>
                <h3 className="text-gray-900 font-semibold group-hover:text-[#6182F7] transition-colors mb-1.5">
                  {measure.measureName}
                </h3>
                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                  {measure.aiData.definition}
                </p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">
                    {measure.aiData.individual.areas.length} AI tools &middot; {measure.aiData.individual.prompts.length} prompts
                  </span>
                  {measure.averageScore != null && (
                    <span className="text-xs text-gray-400">
                      {getScoreLabel(measure.averageScore)} &middot; {measure.scoreCount} assessed
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {filteredMeasures.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500">No measures match your search.</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedMeasure && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8" onClick={closeModal}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 my-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: selectedMeasure.measureColor || "#6699FF" }}
                    />
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${CATEGORY_COLORS[selectedMeasure.aiData.category]?.bg || "bg-gray-100"} ${CATEGORY_COLORS[selectedMeasure.aiData.category]?.text || "text-gray-600"}`}>
                      {selectedMeasure.aiData.category}
                    </span>
                    {selectedMeasure.averageScore != null && (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full text-white ${getScoreColor(selectedMeasure.averageScore)}`}>
                        Team Avg: {selectedMeasure.averageScore} &middot; {getScoreLabel(selectedMeasure.averageScore)}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">{selectedMeasure.measureName}</h2>
                  <p className="text-sm text-gray-500 mt-1 leading-relaxed">{selectedMeasure.aiData.definition}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Score bar */}
              {selectedMeasure.averageScore != null && (
                <div className="mt-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getScoreColor(selectedMeasure.averageScore)}`}
                          style={{ width: `${Math.min(selectedMeasure.averageScore, 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 w-14 text-right">
                      {selectedMeasure.averageScore}/100
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{selectedMeasure.scoreCount} team member{selectedMeasure.scoreCount !== 1 ? "s" : ""} assessed</p>
                </div>
              )}

              {/* Tabs */}
              <div className="flex gap-0 mt-4 -mb-5 border-b border-gray-200">
                {([
                  { key: "areas" as const, label: "AI Tool Categories" },
                  { key: "org" as const, label: "For Your Organization" },
                  { key: "prompts" as const, label: `Prompts (${selectedMeasure.aiData.individual.prompts.length})` },
                ]).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? "border-[#6182F7] text-[#6182F7]"
                        : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
              {/* AI Tool Categories */}
              {activeTab === "areas" && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    AI tool categories that can strengthen {selectedMeasure.measureName.toLowerCase()} for your team members:
                  </p>
                  <div className="space-y-3">
                    {selectedMeasure.aiData.individual.areas.map((area, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4 border-l-3" style={{ borderLeftWidth: 3, borderLeftColor: selectedMeasure.measureColor || "#6699FF" }}>
                        <h4 className="font-medium text-gray-900 mb-1">{area.name}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{area.desc}</p>
                      </div>
                    ))}
                  </div>

                  {/* Pitfall */}
                  <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <h4 className="text-sm font-medium text-amber-800 mb-1">Watch out</h4>
                        <p className="text-sm text-amber-700 leading-relaxed">{selectedMeasure.aiData.pitfall}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Organizational Implementation */}
              {activeTab === "org" && (
                <div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 leading-relaxed">{selectedMeasure.aiData.org.intro}</p>
                  </div>
                  <div className="space-y-3">
                    {selectedMeasure.aiData.org.items.map((item, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-1">{item.name}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Prompts */}
              {activeTab === "prompts" && (
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Ready-to-use prompts your team can copy and adapt for their own work:
                  </p>
                  <div className="space-y-3">
                    {selectedMeasure.aiData.individual.prompts.map((prompt, idx) => (
                      <div key={idx} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-[#6182F7] uppercase tracking-wide">{prompt.context}</span>
                          <button
                            onClick={() => navigator.clipboard.writeText(prompt.text)}
                            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            Copy
                          </button>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed italic">&ldquo;{prompt.text}&rdquo;</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AIIntegrationSection
